import React, { useEffect } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { Link as RouterLink } from 'react-router-dom'

import { Stack, Typography, Tooltip, Link } from '@mui/material'
import { Modal, Button, Input } from '@repo/ui'

import { useCanAccess, useReferrerState } from 'hooks'
import { useCompanyMutationsQuery, useCompanyRoles } from 'hooks/api'
import { useSnackbar } from 'contexts'
import { getErrorMessage } from 'utils'
import { ROUTES } from 'routes'

// Built-in company role slug seeded on the backend (apps/api/src/database/seed.ts) —
// matches the same field RolePage/RolesPage/UsersPage key their role links on.
const MEMBER_ROLE_NAME = 'company_team_member'

interface SignupDomainModalProps {
  open: boolean
  onClose: () => void
  company: any
}

/**
 * Signup domain allowlist — standing company POLICY (VRG-246), relocated out of a
 * below-the-table Card (VRG-298) into its own modal, opened from the "Allowlist"
 * header button. Same field, helper copy, RHF wiring, save path (the company
 * update mutation) and `manage_company` gate as the panel it replaces.
 */
export const SignupDomainModal = ({ open, onClose, company }: SignupDomainModalProps) => {
  const { open: notification } = useSnackbar()
  const { isHasAccess: canManageSignupDomain } = useCanAccess({ action: 'manage_company' })

  // Resolves the built-in Member role so the helper copy below can link to its detail
  // page. Derived path here is /settings/users → "Users", already the correct
  // "← Back to Users" label, so no override is passed.
  const { data: companyRoles } = useCompanyRoles()
  const memberRole = companyRoles?.find((r) => r.name === MEMBER_ROLE_NAME)
  const referrer = useReferrerState()

  const methods = useForm({ mode: 'onChange', defaultValues: { signup_allowed_domain: '' } })

  useEffect(() => {
    if (open && company) {
      methods.reset({ signup_allowed_domain: company.signup_allowed_domain || '' })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, company])

  const { update: updateCompany, isLoading: isUpdatingSignupDomain } = useCompanyMutationsQuery({
    onSuccess: () => {
      notification({ type: 'success', title: 'Signup domain updated' })
      onClose()
    },
    onError: (error) => {
      notification({ type: 'error', title: getErrorMessage(error) })
    },
  })

  const handleClose = () => {
    methods.reset()
    onClose()
  }

  const onSubmit = methods.handleSubmit((data) => {
    if (!company) return
    updateCompany({
      id: company.id,
      name: company.name,
      logo: { file: null, preview_url: company.logo, isUploaded: true },
      country: company.country,
      provider: company.provider,
      signup_allowed_domain: data.signup_allowed_domain.trim() || null,
    })
  })

  return (
    <Modal
      title="Signup domain"
      open={open}
      onClose={handleClose}
      actions={
        <>
          <Button variant="secondary" onClick={handleClose} disabled={isUpdatingSignupDomain}>
            Cancel
          </Button>
          {/* §27: name the specific missing permission, not a vague "you don't have
              permission" — the copy has to be actionable (what to ask an admin for). */}
          <Tooltip title={!canManageSignupDomain ? "You need the 'Manage company' permission. Ask an admin to grant it." : ''}>
            <span>
              <Button
                variant="primary"
                onClick={onSubmit}
                disabled={!canManageSignupDomain || !methods.formState.isDirty || isUpdatingSignupDomain}
                loading={isUpdatingSignupDomain}
              >
                Save
              </Button>
            </span>
          </Tooltip>
        </>
      }
    >
      <Stack component="form" spacing={4} onSubmit={onSubmit}>
        <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
          Anyone who signs up with an email at this domain joins this company automatically
          with the{' '}
          {memberRole ? (
            <Link
              component={RouterLink}
              to={ROUTES.SETTINGS_COMPANY_ROLE(memberRole.name)}
              state={referrer}
              onClick={handleClose}
              underline="always"
              color="inherit"
            >
              Member
            </Link>
          ) : (
            'Member'
          )}{' '}
          role, no invite needed. Leave blank to require an invite for every new user.
        </Typography>
        <Controller
          name="signup_allowed_domain"
          control={methods.control}
          render={({ field }) => (
            <Input
              placeholder="acme.com"
              value={field.value}
              onValueChange={field.onChange}
              disabled={!canManageSignupDomain}
            />
          )}
        />
      </Stack>
    </Modal>
  )
}
